import React, { useRef, useState, useEffect } from "react";
import * as d3 from "d3";
import "../PopularityChart.css";

const PopularityChart = ({ repoData }) => {
  const svgRef = useRef();
  const [toolTip, setToolTip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });

  useEffect(() => {
    const svg = d3.select(svgRef.current); // Get current ref that is rendered
    svg.selectAll("*").remove(); // Removes previous rendered chart by removing the svg elements

    // Chart constraints for arc object
    const width = 500;
    const height = 500;
    const radius = Math.min(width, height) / 2 - 20;
    const innerRadius = radius * 0.4;

    // Get popularity datapoints from repoData
    // const popularityData = repoData?.popularity_group || repoData || {};
    const data = [
      { label: "Stars", value: repoData.star_ratings || 0, color: "#FFD700" },
      { label: "Forks", value: repoData.forks || 0, color: "#4CAF50" },
      { label: "Watchers", value: repoData.watchers || 0, color: "#2196F3" },
    ];

    // Confirm data properties
    console.log("Formatted data object from repoData: ", data);

    // pie
    const pie = d3
      .pie()
      .value((d) => d.value)
      .sort(null);

    // arc
    const arc = d3.arc().innerRadius(innerRadius).outerRadius(radius);
    const arcHover = d3
      .arc()
      .innerRadius(innerRadius)
      .outerRadius(radius + 10); // To create a larger arc on hover

    // Create group for the svg
    const g = svg
      .attr("width", width)
      .attr("height", height)
      .append("g") // Changed from "group" to "g"
      .attr("transform", `translate(${width / 2}, ${height / 2})`); // Added missing )

    // Fix the arcs creation:
    const arcs = g
      .selectAll(".arc")
      .data(pie(data))
      .enter()
      .append("g") // Changed from "group" to "g"
      .attr("class", "arc");

    arcs
      .append("path")
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) {
        d3.select(this).attr("d", arcHover);
        const [x, y] = d3.pointer(event, document.body);
        setToolTip({
          visible: true,
          x: x + 10,
          y: y - 10,
          content: `${d.data.label}: ${d.data.value.toLocaleString()}`,
        });
      })
      .on("mouseout", function (event, d) {
        d3.select(this).attr("d", arc);
        setToolTip({ visible: false, x: 0, y: 0, content: "" });
      })
      .attr("d", (d) => {
        // Each arc starts with no sweep (start angle = end angle)
        return d3
          .arc()
          .innerRadius(innerRadius)
          .outerRadius(radius)
          .startAngle(d.startAngle)
          .endAngle(d.startAngle)(); // No sweep initially
      })
      .transition()
      .delay((d, i) => i * 600) // Each arc waits 600ms
      .duration(800) // Each sweep takes 800ms
      .ease(d3.easeCircleInOut)
      .attr("d", arc); // Animate to full arc

    // Add labels to each arc
    arcs
      .append("text")
      .attr("transform", (d) => `translate(${arc.centroid(d)})`)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .attr("fill", "#ffffff")
      .attr("pointer-events", "none")
      .attr("opacity", 0) // Start invisible
      .text((d) => d.data.label)
      .transition() // Begin animation
      .delay((d, i) => i * 300 + 400) // Labels appear after their slice
      .duration(600) // Shorter duration for labels
      .attr("opacity", 1); // Fade in

    // Center text for the chart
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("font-size", "18px")
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      .attr("dy", "-0.5em")
      .text("Repository");

    // Secondary center text for the chart
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("fill", "#555")
      .attr("dy", "1em")
      .text("Popularity");

    g.selectAll("text")
      .attr("opacity", 0)
      .transition()
      .delay(1200) // Wait for slices to finish
      .duration(500)
      .attr("opacity", 1);
  }, [repoData]);

  return (
    <div style={{ position: "relative" }}>
      <div className="chart-container">
        <svg ref={svgRef} className="chart-svg"></svg>

        {/* Tooltip */}
        {toolTip.visible && (
          <div
            className="tooltip"
            style={{
              left: toolTip.x,
              top: toolTip.y,
              transform: "translate(-50%, -100%)",
            }}
          >
            {toolTip.content}
            <div className="tooltip-arrow"></div>
          </div>
        )}

        {/* Legend */}
        <div className="legend-container">
          <div className="legend-item">
            <div className="legend-color-box legend-color-stars"></div>
            <span className="legend-text">
              Stars: {(repoData.star_ratings || 0).toLocaleString()}
            </span>
          </div>
          <div className="legend-item">
            <div className="legend-color-box legend-color-forks"></div>
            <span className="legend-text">
              Forks: {(repoData.forks || 0).toLocaleString()}
            </span>
          </div>
          <div className="legend-item">
            <div className="legend-color-box legend-color-watchers"></div>
            <span className="legend-text">
              Watchers: {(repoData.watchers || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PopularityChart;
